import React, { useState, useEffect, useCallback, Fragment, useMemo } from 'react';
import EnhancedTable from '../../../components/table';
import Status from '../../../components/status';
import Config from '../../../config';
import { List, ListItemText, ListItem as ListItemBase, Grid as GridBase, Select, Toolbar, Button, ButtonGroup, Popper, Fade, ClickAwayListener, Dialog, DialogContent, DialogTitle, Radio, FormControlLabel, DialogActions, RadioGroup, Typography, CircularProgress, InputLabel, FormControl, IconButton, MenuItem } from '@mui/material';
import { Link, Redirect } from 'react-router-dom';
import Moment from 'react-moment';
import Helpers, { documentSignalrUpdate, getCompanyNames, getStatusOptions, nullableDateTemplate } from '../../../helpers';
import { MoreVert, CheckCircle, LocalShipping, Receipt, AssignmentReturn } from '@mui/icons-material';
import DownloadButton from '../../../components/download_button';
import DocumentLink from '../../../components/document_link';
import DropZone from '../../files/dropzone';
import BulkPick from '../bulk_pick';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CatalogManagerResults from '../../../components/CatalogManagerResults';
import { Root, Header, MoreOptsMenu, IconContainer, defaultResultsContainerStyles, pageContentGap } from '../../../theme';
import EnhancedSelect from '../../../components/EnhancedSelect';
import { utilGetPartners } from '../../../utils/utils';
// Constants
import { ORDER_STATUS, ORDER_ROUTES } from './constants';
// Custom Hooks
import { useOrders } from './hooks/useOrders';
import { useOrderExport } from './hooks/useOrderExport';
import { useOrderFinancing } from './hooks/useOrderFinancing';
import { useBulkActions } from './hooks/useBulkActions';
import { useOrderUpload } from './hooks/useOrderUpload';
// Utils
import { buildOrderFilters } from './utils/orderHelpers';


import VisuallyHidden from '../../../components/VisuallyHidden';

// Relax MUI legacy/v7 components to `any` so their custom props type-check
// (behavior unchanged). Matches pages/orders/order_list.tsx / ack_multiple.tsx:
// Grid's `size` prop and ListItem's legacy `button` prop aren't in the
// currently-typed API.
const Grid: any = GridBase;
const ListItem: any = ListItemBase;

// styled(Typography) strips the OverridableComponent typing so the `component`
// prop isn't visible without this cast. Matches pages/files/attachments_v2.tsx.
const PageHeading = Header as React.ComponentType<any>;

const iconButtonSx = {
    width: "100%",
    justifyContent: "flex-start",
    padding: "12px",
    textTransform: "none",
    borderRadius: "6px",
    border: "none",
    background: "#fff",
    boxShadow: "0px 2px 4px -1px rgba(10, 9, 11, 0.02), 0px 5px 13px -5px rgba(10, 9, 11, 0.05)"
};

/** Option shape rendered by the sender/receiver `EnhancedSelect` partner filter. */
interface PartnerOption {
    label: string;
    value: string | number;
}

interface OrdersPartnerFilterProps {
    table: any;
    partners: PartnerOption[];
    loadingPartners: boolean;
    isSupplier?: boolean;
}

// Partner Filter Component
function OrdersPartnerFilter(props: OrdersPartnerFilterProps) {
    const { table, partners, loadingPartners, isSupplier } = props;
    const [selectedPartner, setSelectedPartner] = useState<PartnerOption | null>(null);

    // Check for existing filters on mount or table state change
    useEffect(() => {
        if (table && table.state) {
            const filters = table.state.filter?.filters || [];
            // Check for the appropriate filter based on user role
            const fieldToCheck = isSupplier ? 'companyName' : 'partnerCompanyName';
            const existingFilter = filters.find(f => f.field === fieldToCheck);

            if (existingFilter) {
                const partner = partners.find(p => p.label === existingFilter.value);
                if (partner) {
                    setSelectedPartner(partner);
                } else {
                    setSelectedPartner(null);
                }
            } else {
                // No partner filter found
                setSelectedPartner(null);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [table?.state?.filter, partners]);

    const handlePartnerChange = (value: PartnerOption | null) => {
        if (!table) return;

        // Filter by sender (companyName) for suppliers, receiver (partnerCompanyName) for retailers
        const fieldToFilter = isSupplier ? 'companyName' : 'partnerCompanyName';

        if (!value) {
            // Remove filter
            if (selectedPartner) {
                table.extensions.onRemoveFilter({
                    field: fieldToFilter,
                    operator: 'eq',
                    value: selectedPartner.label
                });
                setSelectedPartner(null);
            }
        } else {
            // Remove any existing partner filters
            const oppositeField = isSupplier ? 'partnerCompanyName' : 'companyName';
            const oppositeFilter = table.state.filter?.filters?.find(f => f.field === oppositeField);
            if (oppositeFilter) {
                table.extensions.onRemoveFilter(oppositeFilter);
            }

            // Apply new filter
            const oldFilters = table.state.filter?.filters?.filter(
                filter => filter.field !== 'companyName' && filter.field !== 'partnerCompanyName'
            ) || [];

            table.extensions.replaceFilters([
                ...oldFilters,
                {
                    field: fieldToFilter,
                    operator: 'eq',
                    value: value.label
                }
            ]);
            setSelectedPartner(value);
        }
    };

    return (
        <EnhancedSelect
            isClearable
            placeholder={isSupplier ? "Select sender..." : "Select receiver..."}
            containerStyle={{minWidth: "200px"}}
            options={partners}
            value={selectedPartner}
            onChange={handlePartnerChange}
            isLoading={loadingPartners}
            isDisabled={!table || loadingPartners}
        />
    );
}

interface OrderManagementV2Props {
    setTitle: (title: string) => void;
    signalr?: any;
    isRetailer?: boolean;
    isSupplier?: boolean;
    companyName?: string;
    financingEnabled?: boolean;
    financialStatus?: string;
}

interface UiState {
    salesOrdersHidden: boolean;
    redirect: string | null;
    submitting: boolean;
}

interface DialogsState {
    showError: boolean;
    actions: boolean;
}

interface SelectionState {
    actionAnchor: HTMLElement | null;
    actionId: string | number | null;
    actionStatus: number | null;
}

function OrderManagementV2(props: OrderManagementV2Props) {
    const [uiState, setUiState] = useState<UiState>({
        salesOrdersHidden: false,
        redirect: null,
        submitting: false
    });

    const [dialogs, setDialogs] = useState<DialogsState>({
        showError: false,
        actions: false
    });

    const [selectionState, setSelectionState] = useState<SelectionState>({
        actionAnchor: null,
        actionId: null,
        actionStatus: null
    });

    const [error, setError] = useState('');

    // Partner filter type state - always receiver now
    const partnerFilterType = 'receiver';

    // Partner filter state
    const [partners, setPartners] = useState<PartnerOption[]>([]);
    const [loadingPartners, setLoadingPartners] = useState(false);


    const toggleDialog = useCallback((dialogName: keyof DialogsState, value?: boolean) => {
        setDialogs(prev => ({
            ...prev,
            [dialogName]: value !== undefined ? value : !prev[dialogName]
        }));
    }, []);

    const updateUiState = useCallback((updates: Partial<UiState>) => {
        setUiState(prev => ({ ...prev, ...updates }));
    }, []);

    const updateSelectionState = useCallback((updates: Partial<SelectionState>) => {
        setSelectionState(prev => ({ ...prev, ...updates }));
    }, []);

    const showError = useCallback((errorMessage: string) => {
        setError(errorMessage);
        toggleDialog('showError', true);
    }, [toggleDialog]);

    const {
        selectedOrders,
        selectedModels,
        selectedCount,
        selectedFailed,
        handleSelection
    } = useOrders();

    const refreshOrders = useCallback(() => {
        setConfig(prev => ({ ...prev, refresh: !prev.refresh }));
    }, []);

    const {
        exportState,
        exporting,
        showExportDialog,
        hideExportDialog,
        updateExportState,
        exportOrders,
        downloadPackingSlips
    } = useOrderExport();

    const {
        financeLoading,
        financeError,
        clearFinanceError,
        shouldShowFinancing,
        getFinanceButtonText,
        initializeFinancing,
        financeOrders,
        setFinanceSelected,
        getFinancialStatusColor,
        showFinanceDialog,
        toggleFinanceDialog
    } = useOrderFinancing(props.financialStatus);

    const {
        bulkActionState,
        processing,
        showBulkRetry,
        showBulkActions,
        showBulkRetryDialog,
        showBulkIgnoreDialog,
        showBulkCancelDialog,
        executeBulkAction,
        toggleBulkActions,
        toggleBulkRetry
    } = useBulkActions();

    const {
        uploadState,
        showUploadDialog,
        showUploadResults,
        uploadTracking,
        setUploadFile,
        showUpload,
        hideUpload,
        setTrackingImportRef
    } = useOrderUpload();

    useEffect(() => {
        if (props.setTitle) props.setTitle('Orders');
    }, [props]);

    useEffect(() => {
        if (financeError) {
            showError(financeError);
            clearFinanceError();
        }
    }, [financeError, showError, clearFinanceError]);

    const fulfillSelected = () => {
        updateUiState({ redirect: `${ORDER_ROUTES.FULFILL}?orderids=${selectedOrders.join(',')}` });
    };

    const invoiceSelected = () => {
        updateUiState({ redirect: `${ORDER_ROUTES.INVOICE}?orderids=${selectedOrders.join(',')}` });
    };

    const ackSelected = () => {
        updateUiState({ redirect: `${ORDER_ROUTES.ACCEPT}?orderids=${selectedOrders.join(',')}` });
    };

    const handleShowBulkRetry = useCallback(() => {
        showBulkRetryDialog(selectedModels, selectedFailed);
    }, [showBulkRetryDialog, selectedModels, selectedFailed]);

    const handleShowBulkIgnore = useCallback(() => {
        showBulkIgnoreDialog(selectedModels, selectedFailed);
    }, [showBulkIgnoreDialog, selectedModels, selectedFailed]);

    const handleShowBulkCancel = useCallback(() => {
        showBulkCancelDialog(selectedModels);
    }, [showBulkCancelDialog, selectedModels]);

    const handleExportClick = useCallback(() => {
        showExportDialog();
    }, [showExportDialog]);

    const handleToggleBulkActions = useCallback(() => {
        toggleBulkActions(true);
    }, [toggleBulkActions]);

    /* We have this seperate logic for filters as these filters are "outside" of the table component.
    *  This is because the table component is not aware of the filters and will not update when the filters change.
    *  This is a workaround to ensure that the filters are updated when the filters change. */
    const filters = useMemo(() => {
        const queryParams = new Helpers().queryString();
        return buildOrderFilters({
            hideSalesOrders: uiState.salesOrdersHidden,
            companyName: props.companyName,
            status: queryParams['status'],
            statusMax: queryParams['statusmax'],
            statusCode: queryParams['statuscode']
        });
    }, [uiState.salesOrdersHidden, props.companyName]);

    useEffect(() => {
        setConfig(prev => ({
            ...prev,
            filter: { logic: 'and', filters }
        }));
    }, [filters]);

    // Load partners on mount
    useEffect(() => {
        const loadPartners = async () => {
            setLoadingPartners(true);
            try {
                const partnersData = await utilGetPartners();
                if (!partnersData.errorMessage) {
                    const formattedPartners = partnersData
                        .sort((a, b) => a.CompanyName.localeCompare(b.CompanyName))
                        .map(p => ({
                            label: p.CompanyName,
                            value: p.Id
                        }));
                    setPartners(formattedPartners);
                }
            } catch (error) {
                console.error('Failed to load partners:', error);
            } finally {
                setLoadingPartners(false);
            }
        };

        loadPartners();
    }, []);

    // Simple hover template to show full text for truncated cells
    const textHoverTemplate = (row, column) => row[column.id] || '';

    const baseColumns = useMemo<any[]>(() => [
        { id: "id", hidden: true },
        { id: 'documentType', hidden: true, type: 'string' },
        { id: 'status', hidden: true, label: 'Status Code', type: 'number' },
        { id: 'internalId', type: 'number', filterable: true, sortable: true, numeric: true, disablePadding: true, label: 'ID', width: '10em' },
        { id: 'partnerPo', type: 'string', filterable: true, label: 'Reference Number', width: '15em', hoverTemplate: textHoverTemplate },
        { id: 'sourceKey', type: 'string', filterable: true, label: 'Source Key', width: '15em', hidden: true, toggleable: true, hoverTemplate: textHoverTemplate },
        { id: 'date', type: 'date', filterable: true, sortable: true, label: 'Date', template: (row, column, colIndex, rowIndex) => (<Moment format='MM/DD/YYYY hh:mm A'>{row.date}</Moment>), width: '15em' },
        { id: 'requestedShipDate', type: 'date', filterable: true, sortable: true, label: 'Requested Ship Date', template: (row, column, colIndex, rowIndex) => (nullableDateTemplate as any)(row.requestedShipDate, row, colIndex, rowIndex), width: '15em', hidden: true, toggleable: true },
        { id: 'statusChangeDate', type: 'date', hidden: true, label: 'Status Change Date', template: (row, column, colIndex, rowIndex) => (nullableDateTemplate as any)(row.statusChangeDate, row, colIndex, rowIndex) },
        { id: 'companyName', type: 'string', filterable: true, label: 'Sender', toggleable: true, getOptions: getCompanyNames, width: '15em', hoverTemplate: textHoverTemplate },
        { id: 'partnerCompanyName', type: 'string', filterable: true, label: 'Receiver', toggleable: true, getOptions: getCompanyNames, width: '15em', hoverTemplate: textHoverTemplate },
        { id: 'statusDescription', type: 'string', filterable: true, label: 'Status', getOptions: getStatusOptions, template: (row, column, colIndex, rowIndex, addFilter) => (<Status description={row.statusDescription} onClick={() => addFilter(isNaN(row.statusDescription) ? { operator: 'eq', value: row.statusDescription, field: 'statusDescription' } : { operator: 'eq', value: row.status, field: 'status' })} />), width: '14em', stopPropagation: true },
        { id: 'subStatusDescription', type: 'string', filterable: true, hidden: true, toggleable: true, label: 'SubStatus', hoverTemplate: textHoverTemplate },
        props.financingEnabled ? { id: 'financialStatus', type: 'string', filterable: true, label: 'Financing Status', width: '14em', toggleable: true, hidden: !props.financialStatus, stopPropagation: true, template: (row, column, colIndex, rowIndex, addFilter) => (!(!row.financialStatus) && <Status description={row.financialStatus} color={getFinancialStatusColor(row.financialStatus)} onClick={() => addFilter({ operator: 'eq', value: row.financialStatus, field: 'financialStatus' })} />) } : { id: 'id', hidden: 'true' },
        { id: 'source', type: 'string', filterable: true, hidden: true, toggleable: true, label: 'System', hoverTemplate: textHoverTemplate },
        {
            filterable: false, command: 'commands', stopPropagation: 'true', width: '8rem', template: (row, column, colIndex, rowIndex) => (
                <Fragment>
                    <Button
                        size='small'
                        variant='outlined'
                        component={Link}
                        to={`${ORDER_ROUTES.ORDER_DETAILS}?orderid=${row.internalId}`}
                        aria-label={`View details for order ${row.internalId}`}
                    >
                        View
                    </Button>
                    <IconButton
                        size='small'
                        style={{ marginLeft: '0.5rem', borderRadius: 100 }}
                        onClick={(e) => {
                            updateSelectionState({ actionAnchor: e.currentTarget, actionId: row.id, actionStatus: row.status });
                            toggleDialog('actions', true);
                        }}
                        aria-label={`Open actions menu for order ${row.internalId}`}
                        aria-haspopup="true"
                        aria-expanded={dialogs.actions && selectionState.actionId === row.id}
                    >
                        <MoreVert />
                    </IconButton>
                </Fragment>
            )
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [props.financingEnabled, props.financialStatus, getFinancialStatusColor, updateSelectionState, toggleDialog]);

    const [config, setConfig] = useState<any>(() => ({
        url: Config.api + "/odata/Company/Functions.DocumentSearch",
        savedSearchEnabled: true,
        keyField: 'id',
        columns: baseColumns,
        order: 'desc',
        orderBy: 'internalId',
        pageSize: 20,
        pageSizes: [10, 20, 50, 100],
        filter: { logic: 'and', filters },
        term: '',
        title: 'Order Management',
        ariaLabel: 'Orders table with sortable columns and selectable rows',
        refresh: false,
        selectable: true,
        filterInUrl: true,
        signalr: props.signalr,
        onSignalrUpdate: documentSignalrUpdate,
        setSelected: handleSelection
    }));

    // Update config when partnerFilterType changes
    useEffect(() => {
        setConfig(prev => ({
            ...prev,
            partnerFilterType: partnerFilterType,
            customFields: {
                ...prev.customFields,
                partnerFilterType: partnerFilterType
            }
        }));
    }, [partnerFilterType]);

    // Note: partnerFilterType is now always 'receiver', so we don't need to sync it from config

    let uploadConfig: any = {
        getData: () => uploadState.results,
        columns: [
            { id: "Level", label: "Result", width: '8em' },
            { command: 'Tracking1', label: 'Shipment ID', width: '8em', template: (value, row, colIndex, rowIndex) => row.Tracking.ASNId ? <DocumentLink target='_blank' id={row.Tracking.ASNId} type='shipment'>{row.Tracking.ASNId}</DocumentLink> : '' },
            { command: 'Tracking2', label: 'Order ID', width: '8em', template: (value, row, colIndex, rowIndex) => row.Tracking.OrderId ? <DocumentLink target='_blank' id={row.Tracking.OrderId} type='order'>{row.Tracking.OrderId}</DocumentLink> : '' },
            { command: 'Tracking3', label: 'SKU', width: '10em', template: (value, row, colIndex, rowIndex) => row.Tracking.SupplierSKU || row.Tracking.PartnerSKU || row.Tracking.UPC },
            { command: 'Tracking4', label: 'Quantity', width: '8em', template: (value, row, colIndex, rowIndex) => row.Tracking.Quantity },
            { id: 'Message', label: 'Message', template: (value, row, colIndex, rowIndex) => <div style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>{value}</div> },
        ],
        pageSize: 20,
        pageSizes: [10, 20, 50, 100],
        title: 'Tracking Import Results',
        ariaLabel: 'Results of tracking import operation',
        refresh: uploadState.refreshUpload
    };

    let financeConfig: any = {
        url: Config.api + "/odata/Company/Functions.DocumentSearch",
        columns: [
            { id: "id", hidden: true },
            { id: 'status', hidden: true, label: 'Status Code', type: 'number' },
            { id: 'internalId', type: 'number', filterable: true, sortable: true, numeric: true, disablePadding: true, label: 'ID', width: '10em' },
            { id: 'partnerPo', type: 'string', filterable: true, label: 'Reference Number', width: '15em' },
            { id: 'sourceKey', type: 'string', filterable: true, label: 'Source Key', width: '15em', hidden: true, toggleable: true },
            { id: 'date', type: 'date', filterable: true, sortable: true, label: 'Date', template: (value, row, addFilter) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
            { id: 'requestedShipDate', type: 'localdate', filterable: true, sortable: true, label: 'Requested Ship Date', template: (value, row, addFilter) => (nullableDateTemplate as any)(value, row, addFilter), width: '15em', hidden: true, toggleable: true },
            { id: 'companyName', type: 'string', filterable: true, label: 'Sender', toggleable: true, getOptions: getCompanyNames },
            { id: 'statusDescription', type: 'string', filterable: true, label: 'Status', getOptions: getStatusOptions, template: (value, row, addFilter) => (<Status description={value} onClick={() => addFilter(isNaN(value) ? { operator: 'eq', value: value, field: 'statusDescription' } : { operator: 'eq', value: row.status, field: 'status' })} />), width: '14em', stopPropagation: true },
            {
                id: 'financialStatus',
                filterable: true,
                label: 'Financing Status',
                width: '14em',
                toggleable: true,
                hidden: !props.financialStatus,
                stopPropagation: true,
                template: (value, row, addFilter) => (!(!value) && <Status description={value} color={getFinancialStatusColor(value)} onClick={() => addFilter({operator: 'eq', value: value, field: 'financialStatus'})}/>)
            },
        ],
        order: 'desc',
        orderBy: 'internalId',
        pageSize: 20,
        pageSizes: [10, 20, 50],
        filter: {
            logic: 'and', filters: [
                { field: "documentType", operator: "eq", value: "Order", system: true },
                { field: "status", operator: "lt", value: ORDER_STATUS.CANCELLED },
                { field: "status", operator: "gt", value: 500 },
                { field: "financialStatus", operator: "eq", value: null }
            ]
        },
        title: 'Order Financing',
        ariaLabel: 'Orders available for financing',
        refresh: false,
        selectable: true,
        filterInUrl: true,
        signalr: props.signalr,
        emptyContent: "No orders available to finance.",
        onSignalrUpdate: documentSignalrUpdate,
        setSelected: setFinanceSelected,
        actions: <div className="tableActions" style={{ flex: 1, display: 'flex', textAlign: 'left' }}>
            <DownloadButton variant='contained' color='primary' onClick={() => initializeFinancing(true)}>Financing dashboard</DownloadButton>
        </div>,
        selectedActions: <div style={{ display: 'flex' }}>
            <Button onClick={financeOrders} color="primary" variant="contained">Finance orders</Button>
        </div>
    };

    const mainContent = (
        <main role="main" aria-labelledby="page-title">
            <Root style={{ gap: pageContentGap, padding: "24px 14px 24px 14px" }}>
                <Grid container justifyContent={"space-between"} direction={"row"} alignItems={"center"} style={{width: "100%"}}>
                    <Grid item style={{ paddingRight: "24px" }}>
                        <PageHeading id="page-title" component="h1">
                            Order Management
                        </PageHeading>
                    </Grid>

                    <Grid item style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }} role="toolbar" aria-label="Page actions">
                        <Button
                            variant='outlined'
                            onClick={refreshOrders}
                            aria-label="Refresh order list"
                        >
                            Refresh
                        </Button>
                        <Button
                            variant='outlined'
                            component={Link}
                            to='/search'
                            aria-label="View all documents"
                        >
                            All Documents
                        </Button>
                        <Button
                            variant='outlined'
                            onClick={showUpload}
                            aria-label="Import tracking information from file"
                        >
                            Import Tracking
                        </Button>
                    {props.isRetailer && (
                        <Button
                            variant='outlined'
                            onClick={() => updateUiState({ salesOrdersHidden: !uiState.salesOrdersHidden })}
                        >
                            {uiState.salesOrdersHidden ? 'Show Sales Orders' : 'Hide Sales Orders'}
                        </Button>
                    )}
                    <BulkPick
                        variant='outlined'
                    />
                    {props.financingEnabled && shouldShowFinancing(props.financialStatus) && (
                        <DownloadButton
                            variant='outlined'
                            color='primary'
                            disabled={financeLoading}
                            onClick={() => initializeFinancing(props.financialStatus === "APPROVED")}
                        >
                            {financeLoading ? (
                                <>
                                    <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} aria-hidden="true" />
                                    <span aria-live="polite" aria-busy="true">Loading financing options...</span>
                                </>
                            ) : getFinanceButtonText(props.financialStatus)}
                        </DownloadButton>
                    )}
                    </Grid>
                </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <EnhancedTable config={config}
                    key={"order_management_table"}
                    template={(table) => (
                        <CatalogManagerResults
                            showSavedSearch={true}
                            savedSearchTableType="orders"
                            customFields={{ partnerFilterType }}
                            bulkActions={
                                <ButtonGroup variant="outlined"
                                    style={{
                                        visibility: selectedCount > 0 ? "visible" : "hidden",
                                        opacity: selectedCount > 0 ? 1 : 0,
                                    }}>
                                    <Button
                                        disabled={!selectedFailed}
                                        onClick={handleShowBulkRetry}
                                        aria-label={`Retry ${selectedFailed} failed orders`}
                                    >
                                        Retry
                                    </Button>
                                    <Button
                                        onClick={handleExportClick}
                                        aria-label={`Export ${selectedCount} selected orders`}
                                    >
                                        Export
                                    </Button>
                                    <Button
                                        onClick={handleToggleBulkActions}
                                        aria-label="Open bulk actions menu"
                                    >
                                        Bulk Actions
                                    </Button>
                                    <DownloadButton
                                        onClick={() => downloadPackingSlips(selectedOrders)}
                                        aria-label={`Download packing slips for ${selectedCount} selected orders`}
                                    >
                                        Download Packing Slips
                                    </DownloadButton>
                                </ButtonGroup>
                            }
                            buttonFilters={
                                <Grid container style={{gap: "0.5em", flex: 1}}>
                                    <OrdersPartnerFilter
                                        table={table}
                                        partners={partners}
                                        loadingPartners={loadingPartners}
                                        isSupplier={props.isSupplier || !props.isRetailer}
                                    />
                                </Grid>
                            }
                            isSelectable={true}
                            disableTermInUrl={false}
                            condensed={false}
                            table={table}
                            config={config}
                            setConfig={setConfig}
                            resultsContainerStyles={defaultResultsContainerStyles}
                            renderButtonFiltersAfter={false}
                        />
                    )}
                />
            </Grid>
            </Root>
        </main>
    );

    if (uiState.redirect) {
        return <Redirect to={uiState.redirect} push={true} />;
    }

    return (
        <>
            <a href="#main-content">
                <VisuallyHidden>
                    Skip to main content
                </VisuallyHidden>
            </a>
            <div id="main-content">
                {mainContent}
            </div>
            <Popper
                open={dialogs.actions}
                anchorEl={selectionState.actionAnchor}
                transition
                placement="left-start"
                role="menu"
                aria-label="Order actions menu"
                style={{zIndex: 1300}}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={() => toggleDialog('actions', false)} mouseEvent="onMouseDown">
                        <Fade {...TransitionProps} timeout={350}>
                            <MoreOptsMenu>
                                {selectionState.actionStatus < ORDER_STATUS.PENDING && (
                                    <Button
                                        sx={iconButtonSx}
                                        onClick={() => toggleDialog('actions', false)}
                                        component={Link}
                                        to={`${ORDER_ROUTES.ACK_DETAILS}?orderid=${selectionState.actionId}`}
                                    >
                                        <IconContainer>
                                            <CheckCircle style={{width: "20px", height: "20px"}}/>
                                            <Typography>Accept/Reject</Typography>
                                        </IconContainer>
                                    </Button>
                                )}
                                {selectionState.actionStatus < ORDER_STATUS.PENDING && (
                                    <Button
                                        sx={iconButtonSx}
                                        onClick={() => toggleDialog('actions', false)}
                                        component={Link}
                                        to={`${ORDER_ROUTES.SHIPMENT_DETAILS}?orderid=${selectionState.actionId}`}
                                    >
                                        <IconContainer>
                                            <LocalShipping style={{width: "20px", height: "20px"}}/>
                                            <Typography>Ship</Typography>
                                        </IconContainer>
                                    </Button>
                                )}
                                {selectionState.actionStatus < ORDER_STATUS.PENDING && (
                                    <Button
                                        sx={iconButtonSx}
                                        onClick={() => toggleDialog('actions', false)}
                                        component={Link}
                                        to={`${ORDER_ROUTES.INVOICE_DETAILS}?orderid=${selectionState.actionId}`}
                                    >
                                        <IconContainer>
                                            <Receipt style={{width: "20px", height: "20px"}}/>
                                            <Typography>Invoice</Typography>
                                        </IconContainer>
                                    </Button>
                                )}
                                <Button
                                    sx={iconButtonSx}
                                    onClick={() => toggleDialog('actions', false)}
                                    component={Link}
                                    to={`${ORDER_ROUTES.RETURN_DETAILS}?orderid=${selectionState.actionId}`}
                                >
                                    <IconContainer>
                                        <AssignmentReturn style={{width: "20px", height: "20px"}}/>
                                        <Typography>Return Items</Typography>
                                    </IconContainer>
                                </Button>
                            </MoreOptsMenu>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
            <Dialog
                maxWidth="sm"
                fullWidth
                open={exportState.showExportDialog || false}
                onClose={hideExportDialog}
                aria-labelledby="export-dialog-title"
                aria-describedby="export-dialog-description"
            >
                <DialogTitle id="export-dialog-title" style={{padding: '0px'}}>
                    <Toolbar className='lbtoolbar'>Export Orders</Toolbar>
                </DialogTitle>
                <DialogContent id="export-dialog-description">
                    <FormControl fullWidth style={{ marginBottom: '16px' }}>
                        <InputLabel id="export-profile-label">Export profile</InputLabel>
                        {exportState.profiles && (
                            <Select
                                labelId="export-profile-label"
                                id="export-profile-select"
                                style={{ marginTop: "10px", marginBottom: "10px" }}
                                displayEmpty
                                value={exportState.selectedProfile}
                                onChange={(event) => updateExportState({ selectedProfile: event.target.value })}
                                aria-required="true"
                            >
                                {exportState.profiles.map(r => <MenuItem key={r.Name} value={r.Name}>{r.Name}</MenuItem>)}
                            </Select>
                        )}
                    </FormControl>
                    <Typography component="legend" variant="body2" style={{ marginBottom: '8px' }}>File type</Typography>
                    <RadioGroup
                        style={{ flexDirection: 'column' }}
                        value={exportState.fileType}
                        onChange={(e) => updateExportState({ fileType: e.target.value })}
                        aria-labelledby="file-type-label"
                        aria-required="true"
                    >
                        <FormControlLabel value='csv' control={<Radio />} label='CSV' />
                        <FormControlLabel value='xlsx' control={<Radio />} label='XLSX' />
                    </RadioGroup>
                </DialogContent>
                <DialogActions>
                    <DownloadButton
                        onError={(e) => { showError(e); updateExportState({ disabled: false }); }}
                        color='primary'
                        handleComplete={() => updateExportState({ disabled: false })}
                        disabled={exportState.disabled || !exportState.selectedProfile || exporting}
                        onClick={() => exportOrders(selectedOrders)}
                    >
                        {exporting ? (
                            <>
                                <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} aria-hidden="true" />
                                <span aria-live="polite" aria-busy="true">Exporting orders...</span>
                            </>
                        ) : 'Download'}
                    </DownloadButton>
                </DialogActions>
            </Dialog>
            <input
                type="file"
                accept='.csv'
                id="trackingImport"
                ref={setTrackingImportRef}
                style={{ display: "none" }}
                onChange={(e) => setUploadFile(e.target.files[0])}
                aria-label="Upload tracking file"
            />
            <Dialog
                open={dialogs.showError}
                onClose={() => toggleDialog('showError', false)}
                aria-labelledby="error-dialog-title"
                aria-describedby="error-dialog-description"
            >
                <DialogTitle id="error-dialog-title" style={{paddingBottom: '0px'}}>
                    <Toolbar className='lbtoolbar' style={{padding: '0px'}}>Error</Toolbar>
                </DialogTitle>
                <DialogContent>
                    <Typography id="error-dialog-description" role="alert">{error}</Typography>
                </DialogContent>
            </Dialog>
            <Dialog
                open={showUploadDialog || false}
                onClose={hideUpload}
                fullWidth
                maxWidth='md'
                aria-labelledby="upload-dialog-title"
                aria-describedby="upload-dialog-description"
            >
                {showUploadResults ? <EnhancedTable config={uploadConfig} /> :
                    <>
                        <DialogTitle id="upload-dialog-title" style={{padding: '0px'}}>
                            <Toolbar className='lbtoolbar'>Import Tracking</Toolbar>
                        </DialogTitle>
                        <DialogContent id="upload-dialog-description" style={{ overflowX: 'hidden' }}>
                            <Typography sx={{
                                marginBottom: "16px"
                            }}>
                                You must include at least these columns: <b>Id, TrackingNumber, CarrierCode</b>
                            </Typography>
                            <Typography sx={{
                                marginBottom: "16px"
                            }}>
                                If you are uploading a file with line-level details you must also include these columns: <b>LineNumber, ItemIdentifier.SupplierSKU, QuantityShipped</b>
                            </Typography>
                            <Typography sx={{
                                marginBottom: "16px"
                            }}>
                                You may optionally provide these columns on either type of file: <b>ShipFromAddress.CompanyName, ShipFromAddress.Address1, ShipFromAddress.Address2, ShipFromAddress.City, ShipFromAddress.State, ShipFromAddress.Zip, ShipFromAddress.Country</b>
                            </Typography>
                            <DropZone
                                accept=".csv, .xlsx, .txt"
                                style={{ width: '100%', height: '10em', marginTop: '0.5em' }}
                                onDrop={files => setUploadFile(files && files.length ? files[0] : null)}
                                aria-label="Drop zone for CSV, XLSX or TXT files"
                            >
                                {({ isDragAccept, isDragReject, acceptedFiles, rejectedFiles }) => {
                                    if (isDragAccept || isDragReject) {
                                        return "Drop here to upload this file.";
                                    }
                                    return uploadState.file ? uploadState.file.name : 'Drop a CSV/XLSX file here or click to choose a file.';
                                }}
                            </DropZone>
                        </DialogContent>
                        <DialogActions>
                            <DownloadButton
                                color='primary'
                                disabled={!uploadState.file || uploadState.uploading}
                                onClick={uploadTracking}
                            >
                                {uploadState.uploading ? (
                                <>
                                    <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} aria-hidden="true" />
                                    <span aria-live="polite" aria-busy="true">Uploading tracking file...</span>
                                </>
                            ) : 'Import'}
                            </DownloadButton>
                        </DialogActions>
                    </>}
            </Dialog>
            <Dialog
                maxWidth="sm"
                fullWidth
                open={showBulkRetry || false}
                onClose={() => toggleBulkRetry(false)}
                aria-labelledby="bulk-action-dialog-title"
                aria-describedby="bulk-action-dialog-description"
            >
                <DialogTitle id="bulk-action-dialog-title" style={{padding: '0px'}}>
                    <Toolbar className='lbtoolbar'>{bulkActionState.action}</Toolbar>
                </DialogTitle>
                <DialogContent>
                    <Typography id="bulk-action-dialog-description">
                        {bulkActionState.target === 1 ? `Are you sure you want to ${(bulkActionState.action || '').toLowerCase()} this order?` : `Are you sure you want to ${(bulkActionState.action || '').toLowerCase()} these ${bulkActionState.target} orders?`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                        <Button
                            disabled={bulkActionState.disableRetry || processing}
                            onClick={() => toggleBulkRetry(false)}
                            color="primary"
                            aria-label="Cancel action"
                        >
                            No
                        </Button>
                        <Button
                            disabled={bulkActionState.disableRetry || processing}
                            onClick={executeBulkAction}
                            color="primary"
                            aria-label="Confirm action"
                        >
                            {processing ? (
                                <>
                                    <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} aria-hidden="true" />
                                    <span aria-live="polite" aria-busy="true">Processing bulk action...</span>
                                </>
                            ) : 'Yes'}
                        </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                maxWidth='xs'
                fullWidth
                open={showBulkActions || false}
                onClose={() => toggleBulkActions(false)}
                aria-labelledby="bulk-actions-dialog-title"
            >
                <DialogTitle id="bulk-actions-dialog-title" style={{padding: '0px'}}>
                    <Toolbar className='lbtoolbar'>Bulk Document Actions</Toolbar>
                </DialogTitle>
                <List style={{ padding: 0 }}>
                    <ListItem button disabled={!selectedOrders.length} onClick={ackSelected} aria-label={`Accept or reject ${selectedOrders.length} selected orders`}>
                        <ListItemText primary="Accept/Reject" secondary={`${selectedOrders.length} selected orders`} />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                    <ListItem button disabled={!selectedOrders.length} onClick={fulfillSelected} aria-label={`Ship or cancel ${selectedOrders.length} selected orders`}>
                        <ListItemText primary="Ship/Cancel" secondary={`${selectedOrders.length} selected orders`} />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                    <ListItem button disabled={!selectedOrders.length} onClick={invoiceSelected} aria-label={`Invoice ${selectedOrders.length} selected orders`}>
                        <ListItemText primary="Invoice" secondary={`${selectedOrders.length} selected orders`} />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                    <ListItem button disabled={!selectedFailed} onClick={handleShowBulkIgnore} aria-label={`Ignore ${selectedFailed} failed orders`}>
                        <ListItemText primary="Ignore" secondary={`${selectedFailed} failed orders`} />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                    <ListItem button disabled={!selectedOrders.length} onClick={handleShowBulkCancel} aria-label={`Quick cancel ${selectedOrders.length} selected orders`}>
                        <ListItemText primary="Quick Cancel" secondary={<>{selectedOrders.length} selected orders will be cancelled and a cancellation will be generated automatically{props.isRetailer ? '' : <>, you can set a default cancel reason from <Link to='/settings/default-ack-properties'>here</Link></>}</>} />
                        <ArrowForwardIosIcon color="action" />
                    </ListItem>
                </List>
            </Dialog>
            <Dialog
                open={showFinanceDialog}
                maxWidth='lg'
                fullWidth
                onClose={() => toggleFinanceDialog(false)}
                disableEnforceFocus
                aria-labelledby="finance-dialog-title"
            >
                <EnhancedTable config={financeConfig} />
            </Dialog>
        </>
    );
}

export default OrderManagementV2;
